import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import awsconfig from './aws-exports';
import { EventProvider } from './context/EventContext';
import './responsive.css';
import Login from './Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Checklist from './pages/Checklist';
import Payments from './pages/Payments';
import PaymentsNew from './pages/PaymentsNew';
import CategoryDetail from './pages/CategoryDetail';
import Budget from './pages/Budget';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';
import Guests from './pages/Guests';
import MessagesPage from './pages/MessagesPage';
import Notifications from './pages/Notifications';
import RoleBasedHeader from './components/RoleBasedHeader';
import ProtectedRoute from './components/ProtectedRoute';
import { useUserProfile } from './hooks/useUserProfile';

// Vendor pages
import VendorDashboard from './pages/vendor/VendorDashboard';
import VendorEvents from './pages/vendor/VendorEvents';
import VendorChecklist from './pages/vendor/VendorChecklist';
import VendorPayments from './pages/vendor/VendorPayments';
import VendorMessagesPage from './pages/MessagesPage';
import VendorSettings from './pages/vendor/VendorSettings';
import VendorEventDetail from './pages/vendor/VendorEventDetail';
import VendorNotifications from './pages/vendor/VendorNotifications';
import VendorNavigation from './components/vendor/VendorNavigation';
import SupportWidget from './components/SupportWidget';

// Configure Amplify
Amplify.configure(awsconfig);

function EversetApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Get dynamic user profile from database
  const { userProfile, loading: profileLoading } = useUserProfile(user?.username);


  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'Arial, sans-serif'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="*" element={<Login onLoginSuccess={setUser} />} />
        </Routes>
      </Router>
    );
  }

  const currentUser = userProfile || user;
  console.log('Debug - currentUser:', currentUser);
  console.log('Debug - userProfile:', userProfile);
  console.log('Debug - user attributes:', user?.attributes);
  
  // Determine user type from Cognito attributes or backend profile
  const userType = currentUser?.userType || user?.attributes?.['custom:userType'];
  const isVendor = userType === 'vendor';
  const isVenue = userType === 'venue';
  console.log('Debug - userType:', userType);
  console.log('Debug - isVendor:', isVendor);
  console.log('Debug - isVenue:', isVenue);
  
  // Default route for redirects only
  const defaultRoute = (isVendor || isVenue ? '/vendor/dashboard' : '/dashboard');

  return (
    <Router>
      <EventProvider user={user}>
        <div>
          {(isVendor || isVenue) ? <VendorNavigation /> : <RoleBasedHeader user={currentUser} onLogout={handleLogout} />}
          <Routes>
            <Route path="/" element={
              profileLoading ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '50vh',
                  fontFamily: 'Arial, sans-serif'
                }}>
                  Loading...
                </div>
              ) : (
                <Navigate to={defaultRoute} replace />
              )
            } />
            
            {/* Client Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client', 'venue']}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/checklist" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <Checklist />
              </ProtectedRoute>
            } />
            <Route path="/budget" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <Payments />
              </ProtectedRoute>
            } />
            <Route path="/payments" element={<Navigate to="/budget" replace />} />
            <Route path="/vendors" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <Vendors />
              </ProtectedRoute>
            } />
            <Route path="/vendors/:vendorId" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <VendorDetail />
              </ProtectedRoute>
            } />
            <Route path="/guests" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <Guests />
              </ProtectedRoute>
            } />
            <Route path="/messages" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <MessagesPage />
              </ProtectedRoute>
            } />
            <Route path="/threads" element={<Navigate to="/messages" replace />} />
            <Route path="/notifications" element={
              <ProtectedRoute user={currentUser} allowedRoles={['client']}>
                <Notifications />
              </ProtectedRoute>
            } />
            
            {/* Vendor Routes */}
            <Route path="/vendor/dashboard" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/vendor/events" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorEvents />
              </ProtectedRoute>
            } />
            <Route path="/vendor/events/:eventId" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorEventDetail />
              </ProtectedRoute>
            } />
            <Route path="/vendor/checklist" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorChecklist />
              </ProtectedRoute>
            } />
            <Route path="/vendor/payments" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorPayments />
              </ProtectedRoute>
            } />
            <Route path="/vendor/messages" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorMessagesPage isVendor={true} />
              </ProtectedRoute>
            } />
            <Route path="/vendor/threads" element={<Navigate to="/vendor/messages" replace />} />
            <Route path="/vendor/settings" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorSettings />
              </ProtectedRoute>
            } />
            <Route path="/vendor/notifications" element={
              <ProtectedRoute user={currentUser} allowedRoles={['vendor', 'venue']}>
                <VendorNotifications />
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to={defaultRoute} replace />} />
          </Routes>
          <SupportWidget />
        </div>
      </EventProvider>
    </Router>
  );
}

export default EversetApp;